import React, { useRef, useEffect, useState } from 'react';
import { useVideo } from '../Context/VideoContext';
import { FaTimes, FaPlay, FaPause } from 'react-icons/fa';
import './DarsVideoPlayer.css';

const GlobalFloatingPlayer = () => {
    const { videoState, closeFloatingVideo, dockVideo, updateVideoState } = useVideo();
    const videoRef = useRef(null);
    const [isHovered, setIsHovered] = useState(false);

    // Drag & Resize State
    const [position, setPosition] = useState({ x: window.innerWidth - 340, y: window.innerHeight - 200 });
    const [size, setSize] = useState({ width: 320, height: 180 });
    const [isDragging, setIsDragging] = useState(false);
    const [isResizing, setIsResizing] = useState(false);
    const [resizeDir, setResizeDir] = useState(null); // 'n', 's', 'e', 'w', 'ne', 'nw', 'se', 'sw'

    // Refs for calculation
    const dragOffset = useRef({ x: 0, y: 0 });
    const dragStartPos = useRef({ x: 0, y: 0 }); // To detect click vs drag
    const resizeStart = useRef({ x: 0, y: 0, w: 0, h: 0, posX: 0, posY: 0 });

    const { url, isFloating, currentTime, isPlaying } = videoState;

    // Sync play/pause
    useEffect(() => {
        if (!videoRef.current) return;
        if (isPlaying && videoRef.current.paused) {
            videoRef.current.play().catch(e => console.log("Auto-play blocked:", e));
        } else if (!isPlaying && !videoRef.current.paused) {
            videoRef.current.pause();
        }
    }, [isPlaying]);

    // Sync time logic
    useEffect(() => {
        if (isFloating && videoRef.current && Math.abs(videoRef.current.currentTime - currentTime) > 2) {
            videoRef.current.currentTime = currentTime;
        }
    }, [isFloating, url]);

    // Initialize position on float start
    useEffect(() => {
        if (isFloating) {
            setPosition(prev => ({
                x: Math.min(prev.x, window.innerWidth - size.width),
                y: Math.min(prev.y, window.innerHeight - size.height)
            }));
        }
    }, [isFloating]);

    // ================= DRAG HANDLERS =================
    const handleMouseDown = (e) => {
        if (e.target.closest('button') || e.target.closest('.resize-handle')) return;

        setIsDragging(true);
        dragOffset.current = {
            x: e.clientX - position.x,
            y: e.clientY - position.y
        };
        dragStartPos.current = { x: e.clientX, y: e.clientY };
    };

    // ================= RESIZE HANDLERS =================
    const handleResizeMouseDown = (e, dir) => {
        e.stopPropagation();
        setIsResizing(true);
        setResizeDir(dir);
        resizeStart.current = {
            x: e.clientX,
            y: e.clientY,
            w: size.width,
            h: size.height,
            posX: position.x,
            posY: position.y
        };
    };

    // ================= GLOBAL MOUSE EVENTS =================
    useEffect(() => {
        const handleMouseMove = (e) => {
            if (isDragging) {
                const newX = e.clientX - dragOffset.current.x;
                const newY = e.clientY - dragOffset.current.y;
                setPosition({ x: newX, y: newY });
            }
            else if (isResizing && resizeDir) {
                const deltaX = e.clientX - resizeStart.current.x;
                const deltaY = e.clientY - resizeStart.current.y;

                let newW = resizeStart.current.w;
                let newH = resizeStart.current.h;
                let newX = resizeStart.current.posX;
                let newY = resizeStart.current.posY;

                // Horizontal Resize
                if (resizeDir.includes('e')) {
                    newW = Math.max(200, resizeStart.current.w + deltaX);
                } else if (resizeDir.includes('w')) {
                    newW = Math.max(200, resizeStart.current.w - deltaX);
                    // Only update X if width actually changed (above min width)
                    if (newW !== 200) newX = resizeStart.current.posX + deltaX;
                }

                // Vertical Resize
                if (resizeDir.includes('s')) {
                    newH = Math.max(112, resizeStart.current.h + deltaY);
                } else if (resizeDir.includes('n')) {
                    newH = Math.max(112, resizeStart.current.h - deltaY);
                    if (newH !== 112) newY = resizeStart.current.posY + deltaY;
                }

                setSize({ width: newW, height: newH });
                setPosition({ x: newX, y: newY });
            }
        };

        const handleMouseUp = (e) => {
            if (isDragging) {
                // Check if it was a click (moved less than 5px)
                const dist = Math.abs(e.clientX - dragStartPos.current.x) + Math.abs(e.clientY - dragStartPos.current.y);
                if (dist < 5) {
                    togglePlay();
                }
            }
            setIsDragging(false);
            setIsResizing(false);
            setResizeDir(null);
        };

        if (isDragging || isResizing) {
            window.addEventListener('mousemove', handleMouseMove);
            window.addEventListener('mouseup', handleMouseUp);
        }

        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isDragging, isResizing, resizeDir]);


    if (!isFloating || !url) return null;

    const handleTimeUpdate = () => {
        if (videoRef.current) {
            updateVideoState({ currentTime: videoRef.current.currentTime });
            localStorage.setItem(`video-time-${url}`, videoRef.current.currentTime);
        }
    };

    const togglePlay = () => {
        const playing = !videoRef.current.paused;
        if (playing) {
            videoRef.current.pause();
            updateVideoState({ isPlaying: false });
        } else {
            videoRef.current.play();
            updateVideoState({ isPlaying: true });
        }
    };

    // Helper for Resize Handles
    const ResizeHandle = ({ cursor, dir, style }) => (
        <div
            className="resize-handle"
            onMouseDown={(e) => handleResizeMouseDown(e, dir)}
            style={{
                position: 'absolute',
                cursor: cursor,
                zIndex: 20,
                ...style
            }}
        />
    );

    return (
        <div
            className="floating-player-wrapper"
            style={{
                position: 'fixed',
                left: `${position.x}px`,
                top: `${position.y}px`,
                width: `${size.width}px`,
                height: `${size.height}px`,
                zIndex: 9999,
                boxShadow: '0 4px 15px rgba(0,0,0,0.3)',
                borderRadius: '12px',
                background: '#000',
                transition: (isDragging || isResizing) ? 'none' : 'box-shadow 0.3s ease',
                cursor: isDragging ? 'grabbing' : 'auto',
                // Important: Need to ensure handles are clickable, so we don't hide overflow initially?
                // But we need borderRadius clipping for video.
                // We'll put handles INSIDE the div but absolute positioned.
            }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            onMouseDown={handleMouseDown}
        >
            {/* Video Container to ensure clipping */}
            <div style={{ width: '100%', height: '100%', overflow: 'hidden', borderRadius: '12px', pointerEvents: 'none' }}>
                <video
                    ref={videoRef}
                    src={url}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    onTimeUpdate={handleTimeUpdate}
                />
            </div>

            {/* Resize Handles (Invisible but active areas around borders) */}
            <ResizeHandle dir="n" cursor="ns-resize" style={{ top: 0, left: 10, right: 10, height: 10 }} />
            <ResizeHandle dir="s" cursor="ns-resize" style={{ bottom: 0, left: 10, right: 10, height: 10 }} />
            <ResizeHandle dir="w" cursor="ew-resize" style={{ left: 0, top: 10, bottom: 10, width: 10 }} />
            <ResizeHandle dir="e" cursor="ew-resize" style={{ right: 0, top: 10, bottom: 10, width: 10 }} />

            {/* Corner Handles */}
            <ResizeHandle dir="nw" cursor="nwse-resize" style={{ top: 0, left: 0, width: 15, height: 15 }} />
            <ResizeHandle dir="ne" cursor="nesw-resize" style={{ top: 0, right: 0, width: 15, height: 15 }} />
            <ResizeHandle dir="sw" cursor="nesw-resize" style={{ bottom: 0, left: 0, width: 15, height: 15 }} />
            <ResizeHandle dir="se" cursor="nwse-resize" style={{ bottom: 0, right: 0, width: 15, height: 15 }} />

            {/* Overlay Controls */}
            <div
                className="floating-controls"
                style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(0,0,0,0.4)',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    padding: '10px',
                    opacity: isHovered || isDragging || isResizing ? 1 : 0,
                    transition: 'opacity 0.2s',
                    pointerEvents: 'auto'
                }}
            >
                {/* Close Button Only */}
                <button
                    onClick={closeFloatingVideo}
                    style={{ alignSelf: 'flex-end', background: 'transparent', border: 'none', color: '#fff', fontSize: '1.2rem', cursor: 'pointer', marginLeft: 'auto', zIndex: 10 }}
                    title="Yopish (Close)"
                >
                    <FaTimes />
                </button>

                {/* Center Controls (Just Play/Pause now, removed docking button) */}
                <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 10 }}>
                    <button onClick={togglePlay} style={{ background: 'transparent', border: 'none', color: '#fff', fontSize: '2rem', cursor: 'pointer' }}>
                        {isPlaying ? <FaPause /> : <FaPlay />}
                    </button>
                    {/* Note: Docking is now only via the sidebar button in DarsSahifa. The user explicitly asked to "remove expand icon from floating video". */}
                </div>

                {/* Bottom Spacer */}
                <div />
            </div>
        </div>
    );
};

export default GlobalFloatingPlayer;
